import clsx from 'clsx';
import useLayout from 'hooks/useLayout';
import {forwardRef, useImperativeHandle, useState} from 'react';

import useProgressBar from './utils';

export type ProgressProps = {
    className?: string;
    state?: ProgressState;
};
export type ProgressRef = {
    setProgress: (amount: number) => void;
};

type ProgressState = 'failure' | 'inProgress' | 'success';

const Progress = forwardRef<ProgressRef, ProgressProps>(
    ({className, state = 'inProgress'}, ref) => {
        const [progress, setProgress] = useState(0);

        const layout = useLayout();

        useImperativeHandle(
            ref,
            () => ({
                setProgress: (value) =>
                    setProgress(Math.min(100, Math.max(0, value))),
            }),
            [],
        );

        const body = (
            <div
                aria-label="progress bar"
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={progress}
                className={clsx(
                    'mb-4 h-4 rounded bg-grey-200 shadow-inner shadow-black/10',
                    className,
                )}
                role="progressbar"
            >
                <div
                    className={clsx(
                        'h-4 border-b border-green-600 transition-all duration-500 dark:bg-blue-500',
                        progress === 100 ? 'rounded' : 'rounded-l',
                        state === 'failure' && 'bg-coral-500',
                        state === 'inProgress' && 'bg-green-500',
                    )}
                    style={{width: `${progress}%`}}
                />
            </div>
        );

        return layout === 'sideBySide' ? (
            <>
                <div>&nbsp;</div>
                <div role="gridcell">{body}</div>
            </>
        ) : (
            body
        );
    },
);

Progress.displayName = 'Progress';

export default Progress;

export {useProgressBar};
